import React from 'react';
import { BudgetAggregateRow } from '../../config';
import { formatCurrency } from '../../../../utils/currency';
import AggregateMonthlyCell from './AggregateMonthlyCell';
import AggregateFeeCell from './AggregateFeeCell';

interface AggregateRowProps {
  row: BudgetAggregateRow;
  fyStartYear: number;
  onTargetSaved: (rowId: number, year: number, month: number, newAmount: number) => void;
  onFeeSaved: (rowId: number, newAmount: number) => void;
  // When the FY25/26 view shows the Pre-FY column, aggregate rows render an
  // empty placeholder so the grid columns line up. The Pre-FY mechanism is
  // per-project only — aggregate totals are user-set per FY so they don't
  // need a migration affordance.
  showPriorColumn?: boolean;
}

/**
 * One aggregate row (RWC or RWD-by-client). The left sticky band mirrors the
 * project-row geometry (4 subcells × `--budget-label-subcols`): the label
 * spans the first 2 subcells (Project + People), the Probable subcell is
 * deliberately empty — these rows are hand-set annual budgets, not fees with
 * awardable phases — and the 4th subcell hosts the editable Total Fee input.
 * Add / remove / reorder live in RwdClientsSettingsModal.
 */
const AggregateRow: React.FC<AggregateRowProps> = ({
  row,
  fyStartYear,
  onTargetSaved,
  onFeeSaved,
  showPriorColumn = false,
}) => {
  return (
    <div className="budget-page__row aggregate-row">
      <div className="budget-page__cell aggregate-row__label">
        <span className="aggregate-row__label-text">{row.label}</span>
        <div className="aggregate-row__fee">
          <AggregateFeeCell
            aggregateRowId={row.id}
            fyStartYear={fyStartYear}
            totalFee={Number(row.total_fee) || 0}
            onFeeSaved={(amt) => onFeeSaved(row.id, amt)}
          />
        </div>
      </div>
      {showPriorColumn && (
        <div className="budget-page__cell budget-page__cell--month" />
      )}
      {row.targets.map((t, idx) => (
        <div
          key={`agg-${row.id}-${t.year}-${t.month}`}
          className="budget-page__cell budget-page__cell--month"
        >
          <AggregateMonthlyCell
            aggregateRowId={row.id}
            year={t.year}
            month={t.month}
            target={Number(t.amount)}
            actual={Number(row.actuals[idx]?.amount ?? 0)}
            onTargetSaved={(y, m, amt) => onTargetSaved(row.id, y, m, amt)}
          />
        </div>
      ))}
      <div
        className={`budget-page__cell budget-page__cell--unalloc ${
          Number(row.unallocated_fee) > 0
            ? 'is-positive'
            : Number(row.unallocated_fee) < 0
              ? 'is-negative'
              : ''
        }`}
        title="Annual budget minus the sum of this FY's monthly targets"
      >
        {formatCurrency(Number(row.unallocated_fee))}
      </div>
    </div>
  );
};

export default AggregateRow;
